import React, { useState, useEffect, useCallback, useMemo } from 'react';
import '../../styles/BuyBoxForm.css';
import Button from '../Button';
import Spinner from '../Spinner';
import Switch from '../Switch';
import GoogleMapsLoader from '../GoogleMapsLoader';
import BuyerZoneDrawMap from '../BuyerZoneDrawMap/BuyerZoneDrawMap';
import { allStates } from '../../defaults';
import { searchCounties, lookupCountyByFips, formatCounty } from '../../data/usCounties';
import { searchCitiesForCounty } from '../../data/usCityCounties';
import { deleteField, doc, getFirestore, updateDoc } from 'firebase/firestore';
import { recordEvent } from '../../functions';
import { normalizeZonesForEditing, serializeZonesForStorage } from '../../functions/geoUtils';
import {
    BUYBOX_PROPERTY_TYPES, US_STATE_CODES,
    parseBuyBoxNum, formatThousands, shortMoney,
    sanitizeInt, sanitizeDecimal, sanitizeYear, rangeChip,
} from '../../utils/buyBoxUtils';
import {
    readStoredFlipMinRoiPercent,
    readStoredCoLivingMinBedroomsPostConversion,
} from '../../utils/strategyParams';

const EXAMPLE_BUYBOX_CHIPS = ['Maricopa County, AZ', 'Under $250k'];

// Shared between both modes. The settings version adds a one-line summary to the
// collapsed header; the onboarding version uses the same component for "More filters".
function CollapsibleField({ title, summary, open, onToggle, children }) {
    return (
        <div className={`buybox-form-sec${open ? ' buybox-form-sec-open' : ''}`}>
            <button type="button" className="buybox-form-sec-head" onClick={onToggle} aria-expanded={open}>
                <span className="body-semibold colour-primary">{title}</span>
                <span className="buybox-form-sec-head-right">
                    {!open && summary
                        ? <span className="label-regular colour-secondary buybox-form-sec-summary">{summary}</span>
                        : null}
                    <span className={`buybox-form-sec-chevron${open ? ' buybox-form-sec-chevron-open' : ''}`} aria-hidden="true">›</span>
                </span>
            </button>
            {open ? <div className="buybox-form-sec-body">{children}</div> : null}
        </div>
    );
}

function MinMax({ label, minVal, maxVal, setMin, setMax, disabled, inputMode = 'numeric' }) {
    return (
        <div className="buybox-form-minmax">
            <span className="label-regular colour-secondary">{label}</span>
            <div className="buybox-form-minmax-inputs">
                <input
                    type="text"
                    inputMode={inputMode}
                    className="buybox-form-input body-regular"
                    placeholder="No min"
                    value={minVal}
                    onChange={(e) => setMin(e.target.value)}
                    disabled={disabled}
                    aria-label={`Minimum ${label}`}
                />
                <span className="label-regular colour-secondary">to</span>
                <input
                    type="text"
                    inputMode={inputMode}
                    className="buybox-form-input body-regular"
                    placeholder="No max"
                    value={maxVal}
                    onChange={(e) => setMax(e.target.value)}
                    disabled={disabled}
                    aria-label={`Maximum ${label}`}
                />
            </div>
        </div>
    );
}

// mode: 'onboarding' | 'settings'
// initialData: the company's current buyBox doc (null while loading in settings)
// ingestionFilter: the company's ingestionFilter doc (for MLS toggle in settings)
// onSaved: called after a successful save (settings: refreshCompany; onboarding: finish())
// onSkip: (onboarding only) called when the user clicks "Skip"
// onSubmit: (optional) when provided, called with (buyBox, ingestionFilter) to PERSIST
//   instead of writing the company doc — lets unauthenticated callers (the trial
//   buy-box page) save via their own endpoint. The caller records its own event.
// hideMap: (optional) hide the "Draw on map" tab and force search-only geography
//   (polygons aren't supported pre-auth for trials).
// matchNote: (optional) overrides the summary-bar note shown once criteria are set
//   (the default mentions credits, which doesn't apply to free trial users).
// hideMLS: (optional) hide the "Include deals from Zillow, Redfin & Realtor.com"
//   toggle — trial users don't have MLS ingestion, so the control is meaningless.
// companyFeatures: the company's data.features. Needed ONLY to read the LEGACY location
// of the two strategy bars. For a company with no buy box, MCP update_analysis_features
// still stores them at data.features.* (it refuses to materialize a buy box), and the
// companyFeatures getters read buyBox.strategyParams first, then fall back to features.
// Without this the form would show a blank bar while a live one was in effect, and its
// "clear → system default" gesture would be silently defeated by the fallback.
function BuyBoxForm({ mode, companyId, initialData, ingestionFilter, companyFeatures, onSaved, onSkip, onNext, onboardingStep, onSubmit, hideMap, matchNote, hideMLS, autoOpenMap }) {
    // Geography
    const [selectedStates, setSelectedStates] = useState([]);
    const [locQuery, setLocQuery] = useState('');
    const [zipCodes, setZipCodes] = useState([]);
    const [counties, setCounties] = useState([]);
    const [polygons, setPolygons] = useState([]);
    // Reciprocal highlight between the "Map zones" chips and the map badges:
    // hovered = transient emphasis, focused = the zone the map panned to.
    const [hoveredZoneIndex, setHoveredZoneIndex] = useState(null);
    const [focusedZoneIndex, setFocusedZoneIndex] = useState(null);
    // Default to the map tab unless the map is hidden. autoOpenMap (set by the MCP
    // `draw_buy_box_zones` deep link) forces the map tab even if the default ever
    // changes — making the open-on-map intent explicit and robust.
    const [locMode, setLocMode] = useState((autoOpenMap && !hideMap) ? 'map' : (hideMap ? 'search' : 'map'));

    // Price & type
    const [priceMin, setPriceMin] = useState('');
    const [priceMax, setPriceMax] = useState(mode !== 'settings' ? '500,000' : '');
    const [selectedTypes, setSelectedTypes] = useState([]);

    // Size & age
    const [minBeds, setMinBeds] = useState('');
    const [maxBeds, setMaxBeds] = useState('');
    const [minBaths, setMinBaths] = useState('');
    const [maxBaths, setMaxBaths] = useState('');
    const [minSqft, setMinSqft] = useState('');
    const [maxSqft, setMaxSqft] = useState('');
    const [minYear, setMinYear] = useState('');
    const [maxYear, setMaxYear] = useState('');

    // Settings-only
    // Exit strategy — what the company would DO with a house they keep. This is a
    // declaration of intent, deliberately NOT inferred from the comps/co-living
    // analysis toggles (a wholesaler enables those to underwrite for their buyers).
    // It's what tells us whether they buy for themselves. See functions/utils/companyPersona.js.
    const [strategies, setStrategies] = useState([]);
    const [flipMinRoi, setFlipMinRoi] = useState('');
    const [coLivingMinBeds, setCoLivingMinBeds] = useState('');
    const [stratOpen, setStratOpen] = useState(false);

    const [includeMLS, setIncludeMLS] = useState(true);
    const [savedFlash, setSavedFlash] = useState(false);

    // Shared UI
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Accordion open states (settings mode)
    const [locOpen, setLocOpen] = useState(true);
    const [propOpen, setPropOpen] = useState(false);
    const [sizeOpen, setSizeOpen] = useState(false);

    // Collapsible for beds/baths/sqft/year (onboarding mode)
    const [moreOpen, setMoreOpen] = useState(false);

    // Seed MLS toggle from ingestionFilter (settings only)
    useEffect(() => {
        setIncludeMLS(ingestionFilter?.includeMLS !== false);
    }, [ingestionFilter]);

    // Seed form fields from the loaded buy-box doc
    useEffect(() => {
        if (!initialData) return;
        const geo = initialData.geography || {};
        setSelectedStates(Array.isArray(geo.states) ? geo.states : []);
        setZipCodes(Array.isArray(geo.zipCodes) ? geo.zipCodes : []);
        setCounties(Array.isArray(geo.counties) ? geo.counties : []);
        setPolygons(normalizeZonesForEditing(geo.polygons));
        const pr = initialData.priceRange || {};
        setPriceMin(pr.min != null ? formatThousands(String(pr.min)) : '');
        setPriceMax(pr.max != null ? formatThousands(String(pr.max)) : '');
        setSelectedTypes(Array.isArray(initialData.propertyTypes) ? initialData.propertyTypes : []);
        const reqs = initialData.propertyRequirements || {};
        setMinBeds(reqs.minBedrooms != null ? String(reqs.minBedrooms) : '');
        setMaxBeds(reqs.maxBedrooms != null ? String(reqs.maxBedrooms) : '');
        setMinBaths(reqs.minBathrooms != null ? String(reqs.minBathrooms) : '');
        setMaxBaths(reqs.maxBathrooms != null ? String(reqs.maxBathrooms) : '');
        setMinSqft(reqs.minSquareFeet != null ? formatThousands(String(reqs.minSquareFeet)) : '');
        setMaxSqft(reqs.maxSquareFeet != null ? formatThousands(String(reqs.maxSquareFeet)) : '');
        setMinYear(reqs.minYearBuilt != null ? String(reqs.minYearBuilt) : '');
        setMaxYear(reqs.maxYearBuilt != null ? String(reqs.maxYearBuilt) : '');

        // Declared exit strategy + its bars. `strategyParams.co_living.minParkingSpaces`
        // is an ANALYSIS setting (MCP-managed) and is deliberately not edited here.
        //
        // Seed each bar from its STORED value via the mirrored declaration-owned readers
        // (src/utils/strategyParams.js — sync-guarded against the companyFeatures
        // getters). buyBox.strategyParams first, legacy data.features second — but once
        // the strategy is DECLARED the buy box owns the bar and the legacy value is
        // ignored: a declared-and-cleared bar means "system default", and re-seeding it
        // from the stale features value would display the old bar and silently write it
        // back on the next save. null renders blank (= default applies).
        const declared = Array.isArray(initialData.strategies) ? initialData.strategies : [];
        setStrategies(declared);
        const flipRoi = readStoredFlipMinRoiPercent(initialData, companyFeatures);
        const clBeds = readStoredCoLivingMinBedroomsPostConversion(initialData, companyFeatures);
        setFlipMinRoi(flipRoi != null ? String(flipRoi) : '');
        setCoLivingMinBeds(clBeds != null ? String(clBeds) : '');
        if (mode === 'settings' && declared.length > 0) setStratOpen(true);

        // In settings, pre-open collapsed sections that already have data
        if (mode === 'settings') {
            if (reqs.minBedrooms != null || reqs.maxBedrooms != null || reqs.minBathrooms != null ||
                reqs.maxBathrooms != null || reqs.minSquareFeet != null || reqs.maxSquareFeet != null ||
                reqs.minYearBuilt != null || reqs.maxYearBuilt != null) {
                setSizeOpen(true);
            }
            if (pr.min != null || pr.max != null ||
                (Array.isArray(initialData.propertyTypes) && initialData.propertyTypes.length > 0)) {
                setPropOpen(true);
            }
            const hasSearchGeo = (Array.isArray(geo.states) && geo.states.length > 0) ||
                (Array.isArray(geo.zipCodes) && geo.zipCodes.length > 0) ||
                (Array.isArray(geo.counties) && geo.counties.length > 0);
            if (hasSearchGeo) {
                setLocMode('search');
            }
        }
    }, [initialData, mode, companyFeatures]);

    // Location typeahead
    const stateOptions = useMemo(() => allStates.filter(s => US_STATE_CODES.includes(s.abbreviation)), []);
    const stateNameByAbbr = useMemo(
        () => Object.fromEntries(stateOptions.map(s => [s.abbreviation, s.name])),
        [stateOptions],
    );
    const locTrimmed = locQuery.trim().toLowerCase();
    const stateSuggestions = locTrimmed
        ? stateOptions
            .filter(s => !selectedStates.includes(s.abbreviation) &&
                (s.name.toLowerCase().includes(locTrimmed) || s.abbreviation.toLowerCase().startsWith(locTrimmed)))
            .slice(0, 6)
        : [];
    const locDigits = locQuery.replace(/\D/g, '');
    const zipSuggestion = locDigits.length === 5 && !zipCodes.includes(locDigits) ? locDigits : null;
    const countySuggestions = locTrimmed.length >= 2
        ? searchCounties(locQuery.trim(), 5).filter(c => !counties.includes(c.fips))
        : [];
    const cityCountySuggestions = (() => {
        if (locTrimmed.length < 2) return [];
        const usedFips = new Set([...counties, ...countySuggestions.map(cs => cs.fips)]);
        const results = [];
        for (const c of searchCitiesForCounty(locQuery.trim(), 20)) {
            if (results.length >= 5) break;
            if (!usedFips.has(c.fips)) { usedFips.add(c.fips); results.push(c); }
        }
        return results;
    })();

    const toggleState = (abbr) => setSelectedStates(prev =>
        prev.includes(abbr) ? prev.filter(s => s !== abbr) : [...prev, abbr]);
    const addZip = (z) => { if (z.length === 5 && !zipCodes.includes(z)) setZipCodes(prev => [...prev, z]); };
    const removeZip = (z) => setZipCodes(prev => prev.filter(v => v !== z));
    const addCounty = (fips) => { if (!counties.includes(fips)) setCounties(prev => [...prev, fips]); };
    const removeCounty = (fips) => setCounties(prev => prev.filter(f => f !== fips));
    const toggleType = (key) => setSelectedTypes(prev =>
        prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);

    const addPolygon = useCallback((poly) => {
        const _key = `p${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
        setPolygons(prev => [...prev, { ...poly, _key }]);
    }, []);
    const removePolygon = useCallback((i) => setPolygons(prev => prev.filter((_, idx) => idx !== i)), []);
    const editPolygon = useCallback((i, geometry) =>
        setPolygons(prev => prev.map((p, idx) => idx === i ? { ...p, geometry } : p)), []);

    // Clear zone highlight when zones are added/removed — indices shift on
    // delete, and a stale focus would otherwise light up an unrelated (e.g.
    // freshly drawn) zone. Editing a shape keeps the count, so focus persists.
    useEffect(() => {
        setHoveredZoneIndex(null);
        setFocusedZoneIndex(null);
    }, [polygons.length]);

    const handleLocKeyDown = (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        if (zipSuggestion) { addZip(zipSuggestion); setLocQuery(''); return; }
        if (stateSuggestions.length > 0) { toggleState(stateSuggestions[0].abbreviation); setLocQuery(''); return; }
        if (countySuggestions.length > 0) { addCounty(countySuggestions[0].fips); setLocQuery(''); return; }
        if (cityCountySuggestions.length > 0) { addCounty(cityCountySuggestions[0].fips); setLocQuery(''); }
    };

    // Derived chip values
    const pMin = parseBuyBoxNum(priceMin);
    const pMax = parseBuyBoxNum(priceMax);
    const priceChip = pMin != null && pMax != null ? `${shortMoney(pMin)}–${shortMoney(pMax)}`
        : pMin != null ? `${shortMoney(pMin)}+`
        : pMax != null ? `Under ${shortMoney(pMax)}` : null;
    const typeChips = selectedTypes
        .map(k => BUYBOX_PROPERTY_TYPES.find(x => x.key === k)?.label)
        .filter(Boolean);
    const bedsChip = rangeChip(parseBuyBoxNum(minBeds), parseBuyBoxNum(maxBeds), ' beds');
    const bathsChip = rangeChip(parseBuyBoxNum(minBaths), parseBuyBoxNum(maxBaths), ' baths');
    const sMin = parseBuyBoxNum(minSqft);
    const sMax = parseBuyBoxNum(maxSqft);
    const sqftChip = sMin != null && sMax != null ? `${sMin.toLocaleString()}–${sMax.toLocaleString()} sqft`
        : sMin != null ? `${sMin.toLocaleString()}+ sqft`
        : sMax != null ? `Up to ${sMax.toLocaleString()} sqft` : null;
    const yMin = parseBuyBoxNum(minYear);
    const yMax = parseBuyBoxNum(maxYear);
    const yearChip = yMin != null && yMax != null ? `Built ${yMin}–${yMax}`
        : yMin != null ? `Built ${yMin}+`
        : yMax != null ? `Built ≤${yMax}` : null;

    const countyLabel = (fips) => {
        const c = lookupCountyByFips(fips);
        return c ? formatCounty(c) : fips;
    };

    const hasAnyCriteria = selectedStates.length > 0 || zipCodes.length > 0 ||
        counties.length > 0 || polygons.length > 0 ||
        priceMin.trim() !== '' || priceMax.trim() !== '' || selectedTypes.length > 0 ||
        [minBeds, maxBeds, minBaths, maxBaths, minSqft, maxSqft, minYear, maxYear].some(v => v.trim() !== '');

    const criteriaChips = [
        ...selectedStates.map(s => stateNameByAbbr[s] || s),
        ...counties.map(countyLabel),
        ...zipCodes,
        ...polygons.map((p, i) => p.name || `Zone ${i + 1}`),
        ...(priceChip ? [priceChip] : []),
        ...typeChips,
        ...[bedsChip, bathsChip, sqftChip, yearChip].filter(Boolean),
    ];
    const funnelChips = hasAnyCriteria ? criteriaChips : EXAMPLE_BUYBOX_CHIPS;

    // One-line summaries for collapsed section headers
    const locationParts = [];
    if (selectedStates.length) locationParts.push(selectedStates.map(s => stateNameByAbbr[s] || s).join(', '));
    if (counties.length) locationParts.push(`${counties.length} count${counties.length > 1 ? 'ies' : 'y'}`);
    if (zipCodes.length) locationParts.push(`${zipCodes.length} ZIP${zipCodes.length > 1 ? 's' : ''}`);
    if (polygons.length) locationParts.push(`${polygons.length} zone${polygons.length > 1 ? 's' : ''}`);
    const locationSummary = locationParts.length ? locationParts.join(' · ') : 'Anywhere';
    const propertySummary = `${priceChip || 'Any price'} · ${typeChips.length ? typeChips.join(', ') : 'Any type'}`;
    const sizeParts = [bedsChip, bathsChip, sqftChip, yearChip].filter(Boolean);
    const sizeSummary = sizeParts.length ? sizeParts.join(' · ') : 'Any beds, baths or size';

    // Collapsed-header summary for the exit strategy. "Passing deals to buyers" is the
    // meaningful empty state — no declared strategy means they don't buy for themselves.
    const strategyLabels = strategies
        .map(s => (s === 'fix_and_flip' ? 'Fix & flip' : s === 'co_living' ? 'Co-living' : null))
        .filter(Boolean);
    const strategySummary = strategyLabels.length
        ? strategyLabels.join(' · ')
        : 'Passing deals to buyers';

    // Range validation
    const invalidRanges = [
        ['Price', priceMin, priceMax],
        ['Bedrooms', minBeds, maxBeds],
        ['Bathrooms', minBaths, maxBaths],
        ['Square feet', minSqft, maxSqft],
        ['Year built', minYear, maxYear],
    ].filter(([, lo, hi]) => {
        const a = parseBuyBoxNum(lo);
        const b = parseBuyBoxNum(hi);
        return a != null && b != null && a > b;
    }).map(([label]) => label);
    const rangeError = invalidRanges.length
        ? `${invalidRanges.join(', ')}: the "to" value must be the same or higher than the "from" value.`
        : '';

    const handleSave = async () => {
        if (saving || rangeError) return;
        if (!companyId && !onSubmit) return; // need a persistence target
        setSaving(true);
        setError('');
        setSavedFlash(false);

        const mlsVal = mode === 'settings' ? includeMLS : true;

        try {
            const buyBox = {
                isCompanyBuyBox: true,
                geography: {
                    states: selectedStates,
                    zipCodes,
                    counties,
                    polygons: serializeZonesForStorage(polygons),
                },
                priceRange: { min: parseBuyBoxNum(priceMin), max: parseBuyBoxNum(priceMax) },
                propertyTypes: selectedTypes.length > 0 ? selectedTypes : null,
                propertyRequirements: {
                    minBedrooms: parseBuyBoxNum(minBeds),
                    maxBedrooms: parseBuyBoxNum(maxBeds),
                    minBathrooms: parseBuyBoxNum(minBaths),
                    maxBathrooms: parseBuyBoxNum(maxBaths),
                    minSquareFeet: parseBuyBoxNum(minSqft),
                    maxSquareFeet: parseBuyBoxNum(maxSqft),
                    minYearBuilt: parseBuyBoxNum(minYear),
                    maxYearBuilt: parseBuyBoxNum(maxYear),
                },
                // Carried on the object so the onSubmit (trial / signup carry-over) path
                // persists the declared exit strategy too — the form shows this section in
                // trial mode, so silently dropping the answer would be a lie.
                strategies,
                strategyParams: {
                    ...(strategies.includes('fix_and_flip') && parseBuyBoxNum(flipMinRoi) != null
                        ? { fix_and_flip: { minRoiPercent: parseBuyBoxNum(flipMinRoi) } } : {}),
                    ...(strategies.includes('co_living') && parseBuyBoxNum(coLivingMinBeds) != null
                        ? { co_living: { minBedroomsPostConversion: parseBuyBoxNum(coLivingMinBeds) } } : {}),
                },
            };
            const nextEnabled = hasAnyCriteria || mlsVal === false;
            const nextIngestionFilter = { ...(ingestionFilter || {}), includeMLS: mlsVal, enabled: nextEnabled };
            if (onSubmit) {
                // Caller owns persistence (e.g. the trial page POSTs to /v1/trial-buybox
                // instead of writing the company doc) and records its own event.
                await onSubmit(buyBox, nextIngestionFilter);
            } else {
                // Write the structural keys individually rather than replacing the whole
                // data.buyBox map: the buy box also carries the company's exit-strategy
                // fields (strategyParams / strategies), which this form only partially
                // edits. A wholesale `'data.buyBox': buyBox` would silently wipe the
                // customer's flip-ROI and co-living parking/bedroom bars.
                // Each key is still replaced wholesale, so clearing values still works.
                //
                // The legacy criteria below (copied onto the buy box by
                // scripts/migrateCompanyBuyBoxOffBuyers.js) are explicitly deleted, exactly
                // as the old wholesale replace did. They're invisible in this form yet the
                // matcher scores them via its `...buyBox` overlay, so preserving them would
                // silently change matching outcomes and break "what you see is what filters".
                // Declared exit strategy + its bars.
                //
                // A bar is touched ONLY while its strategy is ticked. The bars are also
                // settable independently (MCP update_analysis_features writes
                // strategyParams.* without ever setting `strategies`), so deleting a bar
                // just because its box is unticked would silently wipe a value the
                // customer configured in chat — every company starts with strategies: []
                // until they declare here, so that would hit almost all of them.
                //   ticked + value → write   ticked + blank → delete (system default)
                //   unticked       → leave the stored bar completely alone
                // strategyParams.co_living.minParkingSpaces (an analysis setting managed
                // elsewhere) is never touched by any of these paths.
                const buyBoxUpdate = {
                    'data.buyBox.isCompanyBuyBox': true,
                    'data.buyBox.geography': buyBox.geography,
                    'data.buyBox.priceRange': buyBox.priceRange,
                    'data.buyBox.propertyTypes': buyBox.propertyTypes,
                    'data.buyBox.propertyRequirements': buyBox.propertyRequirements,
                    'data.buyBox.strategies': strategies,
                    // Legacy criteria copied on by scripts/migrateCompanyBuyBoxOffBuyers.js.
                    // Explicitly deleted, exactly as the old wholesale replace did: they're
                    // invisible in this form yet the matcher scores them via its `...buyBox`
                    // overlay, so preserving them would silently change matching outcomes.
                    'data.buyBox.profitThresholds': deleteField(),
                    'data.buyBox.rehabLevels': deleteField(),
                    'data.buyBox.dealStructure': deleteField(),
                    'data.ingestionFilter': nextIngestionFilter,
                };
                // Deliberately does NOT touch data.features.* — that map gates PAID
                // analyses and no frontend writes it (rules may well forbid it, which
                // would fail this whole save). Retiring the legacy bar is handled in
                // companyFeatures.js instead: once buyBox.strategies DECLARES a strategy,
                // the buy box owns its bar and the getters stop falling back to
                // data.features. So a blank ("use the system default") save deletes the
                // buyBox key and correctly resolves to the default.
                if (strategies.includes('fix_and_flip')) {
                    const roiNum = parseBuyBoxNum(flipMinRoi);
                    buyBoxUpdate['data.buyBox.strategyParams.fix_and_flip.minRoiPercent'] =
                        roiNum != null ? roiNum : deleteField();
                }
                if (strategies.includes('co_living')) {
                    const bedsNum = parseBuyBoxNum(coLivingMinBeds);
                    buyBoxUpdate['data.buyBox.strategyParams.co_living.minBedroomsPostConversion'] =
                        bedsNum != null ? bedsNum : deleteField();
                }
                await updateDoc(doc(getFirestore(), 'companies', companyId), buyBoxUpdate);
                recordEvent(mode === 'settings' ? 'integrations_buybox_saved' : 'onboarding_buybox_saved', {
                    enabled: nextEnabled,
                    stateCount: selectedStates.length,
                    zipCount: zipCodes.length,
                    countyCount: counties.length,
                    polygonCount: polygons.length,
                    typeCount: selectedTypes.length,
                    hasPrice: priceMin.trim() !== '' || priceMax.trim() !== '',
                });
            }

            if (mode === 'settings') {
                if (onSaved) await onSaved();
                setSavedFlash(true);
                setTimeout(() => setSavedFlash(false), 2000);
            } else if (onboardingStep === 1 && onNext) {
                // Geography step: persist geography then advance to price step.
                onNext({ buyBox, ingestionFilter: nextIngestionFilter });
            } else if (onSaved) {
                // Onboarding: only advance once the buy box (the credit gate) has
                // actually persisted. On failure we fall through to catch, surface
                // the error, and stay put so the user can retry or explicitly Skip.
                onSaved({ buyBox, ingestionFilter: nextIngestionFilter });
            }
        } catch (err) {
            setError(err?.message || String(err));
        } finally {
            setSaving(false);
        }
    };

    const loading = mode === 'settings' && initialData === null;

    // ── Shared JSX fragments ─────────────────────────────────────────────────────

    const locationBody = (
        <>
            {!hideMap && (
                <div className="buybox-form-segment" role="tablist">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={locMode === 'map'}
                        className={`buybox-form-segment-tab label-bold${locMode === 'map' ? ' buybox-form-segment-tab-active' : ''}`}
                        onClick={() => setLocMode('map')}
                        disabled={saving}
                    >
                        Draw on map
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={locMode === 'search'}
                        className={`buybox-form-segment-tab label-bold${locMode === 'search' ? ' buybox-form-segment-tab-active' : ''}`}
                        onClick={() => setLocMode('search')}
                        disabled={saving}
                    >
                        <span className="buybox-tab-label-full">States, Counties &amp; ZIPs</span>
                        <span className="buybox-tab-label-short">States &amp; ZIPs</span>
                    </button>
                </div>
            )}

            {(locMode === 'search' || hideMap) ? (
                <div className="buybox-form-locsearch">
                    <input
                        type="text"
                        className="buybox-form-input body-regular"
                        placeholder="Search a state, county, or ZIP…"
                        value={locQuery}
                        onChange={e => setLocQuery(e.target.value)}
                        onKeyDown={handleLocKeyDown}
                        disabled={saving}
                    />
                    {(stateSuggestions.length > 0 || countySuggestions.length > 0 || cityCountySuggestions.length > 0 || zipSuggestion) ? (
                        <div className="buybox-form-locsuggest">
                            {stateSuggestions.map(s => (
                                <button
                                    key={`s-${s.abbreviation}`}
                                    type="button"
                                    className="buybox-form-locsuggest-item body-regular colour-primary"
                                    onClick={() => { toggleState(s.abbreviation); setLocQuery(''); }}
                                    disabled={saving}
                                >
                                    {s.name} <span className="label-regular colour-secondary">State · {s.abbreviation}</span>
                                </button>
                            ))}
                            {countySuggestions.map(c => (
                                <button
                                    key={`c-${c.fips}`}
                                    type="button"
                                    className="buybox-form-locsuggest-item body-regular colour-primary"
                                    onClick={() => { addCounty(c.fips); setLocQuery(''); }}
                                    disabled={saving}
                                >
                                    {formatCounty(c)} <span className="label-regular colour-secondary">County</span>
                                </button>
                            ))}
                            {cityCountySuggestions.map(c => {
                                const county = lookupCountyByFips(c.fips);
                                if (!county) return null;
                                return (
                                    <button
                                        key={`cc-${c.city}-${c.fips}`}
                                        type="button"
                                        className="buybox-form-locsuggest-item body-regular colour-primary"
                                        onClick={() => { addCounty(c.fips); setLocQuery(''); }}
                                        disabled={saving}
                                    >
                                        {formatCounty(county)} <span className="label-regular colour-secondary">{c.city}</span>
                                    </button>
                                );
                            })}
                            {zipSuggestion ? (
                                <button
                                    type="button"
                                    className="buybox-form-locsuggest-item body-regular colour-primary"
                                    onClick={() => { addZip(zipSuggestion); setLocQuery(''); }}
                                    disabled={saving}
                                >
                                    {zipSuggestion} <span className="label-regular colour-secondary">ZIP</span>
                                </button>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            ) : (
                <div className="buybox-form-map-wrap">
                    <GoogleMapsLoader>
                        {(isLoaded) => (
                            <BuyerZoneDrawMap
                                isMapLoaded={isLoaded}
                                polygons={polygons}
                                onPolygonAdd={addPolygon}
                                onPolygonRemove={removePolygon}
                                onPolygonEdit={editPolygon}
                                hoveredZoneIndex={hoveredZoneIndex}
                                focusedZoneIndex={focusedZoneIndex}
                                onZoneHover={setHoveredZoneIndex}
                            />
                        )}
                    </GoogleMapsLoader>
                </div>
            )}

            {selectedStates.length > 0 ? (
                <div className="buybox-form-chipgroup">
                    <span className="label-bold-caps colour-secondary">States</span>
                    <div className="buybox-form-chips">
                        {selectedStates.map(abbr => (
                            <div key={`st-${abbr}`} className="buybox-form-chip body-regular colour-primary">
                                {stateNameByAbbr[abbr] || abbr}
                                <button type="button" className="buybox-form-chip-remove" onClick={() => toggleState(abbr)} disabled={saving} aria-label={`Remove ${stateNameByAbbr[abbr] || abbr}`}>&times;</button>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}
            {counties.length > 0 ? (
                <div className="buybox-form-chipgroup">
                    <span className="label-bold-caps colour-secondary">Counties</span>
                    <div className="buybox-form-chips">
                        {counties.map(fips => (
                            <div key={`co-${fips}`} className="buybox-form-chip body-regular colour-primary">
                                {countyLabel(fips)}
                                <button type="button" className="buybox-form-chip-remove" onClick={() => removeCounty(fips)} disabled={saving} aria-label={`Remove ${countyLabel(fips)}`}>&times;</button>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}
            {zipCodes.length > 0 ? (
                <div className="buybox-form-chipgroup">
                    <span className="label-bold-caps colour-secondary">ZIP codes</span>
                    <div className="buybox-form-chips">
                        {zipCodes.map(zip => (
                            <div key={`zip-${zip}`} className="buybox-form-chip body-regular colour-primary">
                                {zip}
                                <button type="button" className="buybox-form-chip-remove" onClick={() => removeZip(zip)} disabled={saving} aria-label={`Remove ZIP ${zip}`}>&times;</button>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}
            {polygons.length > 0 ? (
                <div className="buybox-form-chipgroup">
                    <span className="label-bold-caps colour-secondary">Map zones</span>
                    <div className="buybox-form-chips">
                        {polygons.map((poly, i) => (
                            <div
                                key={poly._key ?? `zone-${i}`}
                                className={`buybox-form-chip buybox-form-chip--clickable body-regular colour-primary${i === hoveredZoneIndex ? ' is-active' : ''}`}
                                onMouseEnter={() => setHoveredZoneIndex(i)}
                                onMouseLeave={() => setHoveredZoneIndex(null)}
                                onClick={() => setFocusedZoneIndex(i)}
                            >
                                <span className="buybox-form-chip-num xs-label-bold">{i + 1}</span>
                                {poly.name || `Zone ${i + 1}`}
                                <button
                                    type="button"
                                    className="buybox-form-chip-remove"
                                    onClick={(e) => { e.stopPropagation(); removePolygon(i); }}
                                    disabled={saving}
                                    aria-label={`Remove ${poly.name || `Zone ${i + 1}`}`}
                                >&times;</button>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}
        </>
    );

    const priceInputs = (
        <div className="buybox-form-price-row">
            <div className="buybox-form-input-wrap">
                <span className="buybox-form-input-prefix label-regular colour-secondary">$</span>
                <input
                    type="text"
                    inputMode="numeric"
                    className="buybox-form-input buybox-form-input-money body-regular"
                    placeholder="No min"
                    value={priceMin}
                    onChange={e => setPriceMin(formatThousands(e.target.value))}
                    disabled={saving}
                    aria-label="Minimum price"
                />
            </div>
            <span className="label-regular colour-secondary">to</span>
            <div className="buybox-form-input-wrap">
                <span className="buybox-form-input-prefix label-regular colour-secondary">$</span>
                <input
                    type="text"
                    inputMode="numeric"
                    className="buybox-form-input buybox-form-input-money body-regular"
                    placeholder="No max"
                    value={priceMax}
                    onChange={e => setPriceMax(formatThousands(e.target.value))}
                    disabled={saving}
                    aria-label="Maximum price"
                />
            </div>
        </div>
    );

    const typeGrid = (
        <div className="buybox-form-typegrid">
            {BUYBOX_PROPERTY_TYPES.map(t => (
                <button
                    key={t.key}
                    type="button"
                    className={`buybox-form-type-btn label-regular${selectedTypes.includes(t.key) ? ' buybox-form-type-btn--selected' : ''}`}
                    onClick={() => toggleType(t.key)}
                    disabled={saving}
                >
                    {t.label}
                </button>
            ))}
        </div>
    );

    const toggleStrategy = (key) => setStrategies(prev => (
        prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]
    ));

    // "What would you do with a deal you keep?" Leaving this blank is a valid answer:
    // it means they don't buy for themselves (a wholesaler), which is exactly how we
    // decide whether to offer the self-purchase "Save to CRM" action on a deal.
    const strategyFields = (
        <>
            <p className="label-regular colour-secondary margin-x-small">
                What would you do with a deal you keep? Leave blank if you only pass deals to buyers.
            </p>
            <div className="buybox-form-typegrid">
                <button
                    type="button"
                    className={`buybox-form-type-btn label-regular${strategies.includes('fix_and_flip') ? ' buybox-form-type-btn--selected' : ''}`}
                    onClick={() => toggleStrategy('fix_and_flip')}
                    disabled={saving}
                >
                    Fix &amp; flip
                </button>
                <button
                    type="button"
                    className={`buybox-form-type-btn label-regular${strategies.includes('co_living') ? ' buybox-form-type-btn--selected' : ''}`}
                    onClick={() => toggleStrategy('co_living')}
                    disabled={saving}
                >
                    Co-living
                </button>
            </div>
            {strategies.includes('fix_and_flip') && (
                <div className="buybox-form-field">
                    <span className="label-regular colour-secondary">Minimum profit (ROI %)</span>
                    <input
                        className="buybox-form-input body-regular"
                        inputMode="numeric"
                        placeholder="10"
                        value={flipMinRoi}
                        onChange={(e) => setFlipMinRoi(e.target.value.replace(/[^\d]/g, ''))}
                        disabled={saving}
                    />
                </div>
            )}
            {strategies.includes('co_living') && (
                <div className="buybox-form-field">
                    <span className="label-regular colour-secondary">Minimum bedrooms after conversion</span>
                    <input
                        className="buybox-form-input body-regular"
                        inputMode="numeric"
                        placeholder="6"
                        value={coLivingMinBeds}
                        onChange={(e) => setCoLivingMinBeds(e.target.value.replace(/[^\d]/g, ''))}
                        disabled={saving}
                    />
                </div>
            )}
        </>
    );

    const sizeGrid = (
        <div className="buybox-form-minmax-grid">
            <MinMax label="Bedrooms" minVal={minBeds} maxVal={maxBeds} setMin={v => setMinBeds(sanitizeInt(v))} setMax={v => setMaxBeds(sanitizeInt(v))} disabled={saving} />
            <MinMax label="Bathrooms" minVal={minBaths} maxVal={maxBaths} setMin={v => setMinBaths(sanitizeDecimal(v))} setMax={v => setMaxBaths(sanitizeDecimal(v))} disabled={saving} inputMode="decimal" />
            <MinMax label="Square feet" minVal={minSqft} maxVal={maxSqft} setMin={v => setMinSqft(formatThousands(v))} setMax={v => setMaxSqft(formatThousands(v))} disabled={saving} />
            <MinMax label="Year built" minVal={minYear} maxVal={maxYear} setMin={v => setMinYear(sanitizeYear(v))} setMax={v => setMaxYear(sanitizeYear(v))} disabled={saving} />
        </div>
    );

    return (
        <div className="buybox-form" data-mode={mode}>
            {/* Live summary bar — hidden in onboarding where the panel adds noise */}
            {mode !== 'onboarding' && (
                <div className="buybox-form-bar">
                    <div className="buybox-form-bar-chips">
                        {funnelChips.map((chip, i) => (
                            <span
                                key={`${chip}-${i}`}
                                className={`buybox-form-funnel-chip label-bold${hasAnyCriteria ? '' : ' buybox-form-funnel-chip-example'}`}
                            >
                                {chip}
                            </span>
                        ))}
                    </div>
                    <span className="label-regular colour-secondary buybox-form-bar-note">
                        {hasAnyCriteria
                            ? (matchNote || 'Matching deals cost 1 credit (2 if co-living runs) · everything else is free')
                            : 'Example — set yours below'}
                    </span>
                </div>
            )}

            {loading ? (
                <div className="buybox-form-loading"><Spinner /></div>
            ) : (
                <div className="buybox-form-body">
                    {mode === 'settings' ? (
                        <>
                            {/* ① Location */}
                            <CollapsibleField title="Location" summary={locationSummary} open={locOpen} onToggle={() => setLocOpen(o => !o)}>
                                {locationBody}
                            </CollapsibleField>

                            {/* ② Price & property type */}
                            <CollapsibleField title="Price & property type" summary={propertySummary} open={propOpen} onToggle={() => setPropOpen(o => !o)}>
                                <div className="buybox-form-field">
                                    <span className="label-regular colour-secondary">Price</span>
                                    {priceInputs}
                                </div>
                                <div className="buybox-form-field">
                                    <span className="label-regular colour-secondary">Property type</span>
                                    {typeGrid}
                                </div>
                            </CollapsibleField>

                            {/* ③ Beds, baths & size */}
                            <CollapsibleField title="Beds, baths & size" summary={sizeSummary} open={sizeOpen} onToggle={() => setSizeOpen(o => !o)}>
                                {sizeGrid}
                            </CollapsibleField>

                            {/* ④ Exit strategy — declares whether they buy for themselves */}
                            <CollapsibleField title="Exit strategy" summary={strategySummary} open={stratOpen} onToggle={() => setStratOpen(o => !o)}>
                                {strategyFields}
                            </CollapsibleField>

                            {/* MLS toggle */}
                            {hideMLS ? null : (
                                <div className="buybox-form-field buybox-form-mls">
                                    <Switch
                                        val={includeMLS}
                                        setVal={setIncludeMLS}
                                        label="Include deals from Zillow, Redfin & Realtor.com"
                                        disabled={saving}
                                    />
                                </div>
                            )}
                        </>
                    ) : onboardingStep === 1 ? (
                        <div className="buybox-form-field">
                            {locationBody}
                        </div>
                    ) : onboardingStep === 2 ? (
                        <div className="buybox-form-field">
                            <div className="buybox-form-price-row">
                                <div className="buybox-form-input-wrap">
                                    <span className="buybox-form-input-prefix label-regular colour-secondary">$</span>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        className="buybox-form-input buybox-form-input-money body-regular"
                                        placeholder="No max"
                                        value={priceMax}
                                        onChange={e => setPriceMax(formatThousands(e.target.value))}
                                        disabled={saving}
                                        aria-label="Maximum price"
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Location — always visible */}
                            <div className="buybox-form-field">
                                <span className="label-bold colour-primary">Where do you buy?</span>
                                {locationBody}
                            </div>

                            {/* Price — always visible */}
                            <div className="buybox-form-field">
                                <span className="label-bold colour-primary">Asking price</span>
                                {priceInputs}
                            </div>

                            {/* Property type — always visible (promoted out of "More filters") */}
                            <div className="buybox-form-field">
                                <span className="label-bold colour-primary">Property type</span>
                                {typeGrid}
                            </div>

                            {/* Exit strategy — always visible; declares self-purchase intent */}
                            <div className="buybox-form-field">
                                <span className="label-bold colour-primary">Exit strategy</span>
                                {strategyFields}
                            </div>

                            {/* Beds / baths / size / year — collapsible */}
                            <CollapsibleField title="More filters" summary={sizeSummary} open={moreOpen} onToggle={() => setMoreOpen(o => !o)}>
                                {sizeGrid}
                            </CollapsibleField>
                        </>
                    )}

                    {rangeError ? <span className="body-regular colour-error">{rangeError}</span> : null}
                    {error ? <span className="body-regular colour-error">{error}</span> : null}

                    <div className="buybox-form-save-row">
                        <Button
                            buttonType="primary-button"
                            size="large"
                            text={onboardingStep === 1
                                ? (saving ? 'Saving…' : 'Next →')
                                : mode === 'onboarding'
                                    ? (saving ? 'Saving…' : 'Save & continue')
                                    : (savedFlash ? 'Saved' : 'Save buy box')}
                            cta={handleSave}
                            disabled={saving || !!rangeError}
                            leftIcon={null}
                            leftIconClass=""
                            rightIcon={null}
                            rightIconClass=""
                            alt=""
                            standardIcon={false}
                            badge={false}
                            badgeVal={null}
                            badgeBG={null}
                            buttonRef={null}
                            spinner={saving}
                            goldAccent={false}
                        />
                        {mode === 'onboarding' && onSkip ? (
                            <span
                                className="label-regular colour-secondary cursor-pointer"
                                onClick={onSkip}
                            >
                                Skip, I'll set this up later
                            </span>
                        ) : null}
                    </div>
                </div>
            )}
        </div>
    );
}

export default BuyBoxForm;
